import { DEFAULT_LOCALE, type AppLocale } from "@/lib/i18n/constants";
import { CONTENT_LOCALE_OVERRIDES } from "@/lib/content/locales";
import { applyLocaleOverrides } from "@/lib/content/locales/apply-locale-overrides";
import { resolveContent } from "../resolve";
import { defineContentEntry, staticText, text } from "../schema";

const sourceFile = "lib/content/entries/shared-chrome.ts";

export type MainLayoutMessages = {
  readonly skipToContent: string;
  readonly navPrimaryAria: string;
  readonly navFooterAria: string;
  readonly navHome: string;
  readonly navServices: string;
  readonly navAiSearch: string;
  readonly navIndustries: string;
  readonly navAgencies: string;
  readonly navFranchiseMultiLocation: string;
  readonly navCreatorEconomy: string;
  readonly navAdult: string;
  readonly navResources: string;
  readonly navPortfolio: string;
  readonly navPackages: string;
  readonly navInsights: string;
  readonly navAbout: string;
  readonly navWhiteLabel: string;
  readonly navAiAvatar: string;
  readonly navAiVideo: string;
  readonly navFranchise: string;
  readonly navCaseStudies: string;
  readonly navBlogs: string;
  readonly navContactCta: string;
  readonly navContactCtaShort: string;
  readonly openHoursPrefix: string;
  readonly emailLabel: string;
  readonly openEmailLabel: string;
  readonly phoneLabel: string;
  readonly whatsappLabel: string;
  readonly whatsappStartLabel: string;
  readonly themeLabel: string;
  readonly headerPreferencesAria: string;
  readonly personaSelectorAria: string;
  readonly personaSelectorMobileAria: string;
  readonly personaSelectorDesktopAria: string;
  readonly personaSelectorSummary: string;
  readonly scopeConfidenceAria: string;
  readonly scopeConfidenceStage1: string;
  readonly scopeConfidenceStage2: string;
  readonly scopeConfidenceStage3: string;
  readonly scopeConfidenceStage4: string;
  readonly scopeConfidenceReset: string;
  readonly resetPreferences: string;
  readonly preferencesReset: string;
  readonly desktopThemeToggleAria: string;
  readonly themeToggleLightLabel: string;
  readonly themeToggleDarkLabel: string;
  readonly openMenuAria: string;
  readonly closeMenuAria: string;
  readonly logoHomeAria: string;
  readonly mobileShortcutAria: string;
  readonly logoEyebrow: string;
  readonly relatedPathLabel: string;
  readonly seeAlsoLabel: string;
  readonly footerEyebrow: string;
  readonly footerSummary: string;
  readonly footerTrustStatement: string;
  readonly footerServicesGroup: string;
  readonly footerIndustriesGroup: string;
  readonly footerResourcesGroup: string;
  readonly footerCompanyGroup: string;
  readonly footerAvailability: string;
  readonly copyrightPrefix: string;
  readonly copyrightSuffix: string;
  readonly creditsLine: string;
  readonly termsOfService: string;
  readonly privacyPolicy: string;
  readonly editorialPolicy: string;
  readonly contactSeoh: string;
  readonly supportPage: string;
  readonly cancellationPage: string;
  readonly cookiePolicy: string;
  readonly acceptableUsePolicy: string;
  readonly accessibilityStatement: string;
  readonly adultGateEyebrow: string;
  readonly adultGateTitle: string;
  readonly adultGateBody: string;
  readonly adultGateAccept: string;
  readonly adultGateRefuse: string;
  readonly scrollTopAria: string;
};

export type LanguageSwitcherContent = {
  readonly label: string;
  readonly locales: Readonly<Record<AppLocale, string>>;
};

export type IndustryNavGroup = {
  readonly title: string;
  readonly items: readonly {
    readonly label: string;
    readonly href: string;
    readonly snippet: string;
    readonly featured?: boolean;
  }[];
};

export type SharedChromeContent = {
  readonly messages: MainLayoutMessages;
  readonly languageSwitcher: LanguageSwitcherContent;
  readonly industryNavGroups: readonly IndustryNavGroup[];
};

export const sharedChromeEntry = applyLocaleOverrides([defineContentEntry({
  key: "shared:marketing-chrome",
  routeKey: "shared-marketing-chrome",
  routePath: "/",
  templateFamily: "navigation-footer-common-ui",
  baseLocale: DEFAULT_LOCALE,
  ownership: {
    owner: "content-registry",
    sourceFile,
    migratedFrom: ["lib/i18n/messages/mainLayout.ts", "components/LanguageSwitcher.tsx", "lib/content/entries/marketing-content.ts"],
    notes: "Phase 7 shared chrome migration. MainLayout messages remain a bridge for next-intl while the registry owns the source copy.",
  },
  schemaTypes: ["WebSite"],
  requiredFields: [
    { path: "messages.skipToContent", kind: "body" },
    { path: "messages.navPrimaryAria", kind: "body" },
    { path: "messages.navFooterAria", kind: "body" },
    { path: "messages.navHome", kind: "body" },
    { path: "messages.navServices", kind: "body" },
    { path: "messages.navAiSearch", kind: "body" },
    { path: "messages.navIndustries", kind: "body" },
    { path: "messages.navResources", kind: "body" },
    { path: "messages.navContactCta", kind: "cta" },
    { path: "messages.logoHomeAria", kind: "body" },
    { path: "messages.personaSelectorMobileAria", kind: "body" },
    { path: "messages.personaSelectorDesktopAria", kind: "body" },
    { path: "messages.mobileShortcutAria", kind: "body" },
    { path: "messages.relatedPathLabel", kind: "body" },
    { path: "messages.seeAlsoLabel", kind: "body" },
    { path: "messages.scopeConfidenceAria", kind: "body" },
    { path: "messages.scopeConfidenceStage1", kind: "body" },
    { path: "messages.scopeConfidenceStage2", kind: "body" },
    { path: "messages.scopeConfidenceStage3", kind: "body" },
    { path: "messages.scopeConfidenceStage4", kind: "body" },
    { path: "messages.scopeConfidenceReset", kind: "cta" },
    { path: "messages.resetPreferences", kind: "cta" },
    { path: "messages.preferencesReset", kind: "body" },
    { path: "messages.footerSummary", kind: "body" },
    { path: "messages.footerTrustStatement", kind: "body" },
    { path: "messages.footerServicesGroup", kind: "body" },
    { path: "messages.footerIndustriesGroup", kind: "body" },
    { path: "messages.footerResourcesGroup", kind: "body" },
    { path: "messages.footerCompanyGroup", kind: "body" },
    { path: "messages.adultGateEyebrow", kind: "body" },
    { path: "messages.adultGateTitle", kind: "h1" },
    { path: "messages.adultGateBody", kind: "body" },
    { path: "messages.adultGateAccept", kind: "cta" },
    { path: "messages.adultGateRefuse", kind: "cta" },
    { path: "languageSwitcher.label", kind: "body" },
    { path: "languageSwitcher.locales", kind: "body" },
    { path: "industryNavGroups", kind: "body" },
  ],
  seoCriticalFields: [],
  indexability: {
    indexableByDefault: false,
    allowEnglishPreviewFallback: true,
  },
  base: {
    messages: {
      skipToContent: text("Skip to main content"),
      navPrimaryAria: text("Primary navigation"),
      navFooterAria: text("Footer navigation"),
      navHome: text("Home"),
      navServices: text("Services"),
      navAiSearch: text("AI Search"),
      navIndustries: text("Industries"),
      navAgencies: text("Agencies"),
      navFranchiseMultiLocation: text("Franchise / Multi-location"),
      navCreatorEconomy: text("Creator economy"),
      navAdult: text("Adult"),
      navResources: text("Resources"),
      navPortfolio: text("Portfolio"),
      navPackages: text("Packages"),
      navInsights: text("Insights"),
      navAbout: text("About"),
      navWhiteLabel: text("White-label"),
      navAiAvatar: text("AI Avatar"),
      navAiVideo: text("AI Video"),
      navFranchise: text("Franchise"),
      navCaseStudies: text("Case Studies"),
      navBlogs: text("Blogs"),
      navContactCta: text("Book a 20-min scoping call"),
      navContactCtaShort: text("Book a 20-min call"),
      openHoursPrefix: text("Open {hours}"),
      emailLabel: text("Founder email"),
      openEmailLabel: text("Email Menashe"),
      phoneLabel: text("Phone"),
      whatsappLabel: text("WhatsApp"),
      whatsappStartLabel: text("Start chat"),
      themeLabel: text("Theme"),
      headerPreferencesAria: text("Language, audience paths, and theme"),
      personaSelectorAria: text("Audience paths"),
      personaSelectorMobileAria: text("Audience paths (mobile menu)"),
      personaSelectorDesktopAria: text("Audience paths (desktop)"),
      personaSelectorSummary: text("Paths"),
      scopeConfidenceAria: text("Next step toward your brief"),
      scopeConfidenceStage1: text("Next: read the founder's letter"),
      scopeConfidenceStage2: text("Next: run the fit check"),
      scopeConfidenceStage3: text("Next: send your brief"),
      scopeConfidenceStage4: text("Brief sent - Menashe replies within 1 business day"),
      scopeConfidenceReset: text("Reset"),
      resetPreferences: text("Reset preferences"),
      preferencesReset: text("Preferences reset"),
      desktopThemeToggleAria: text("Desktop theme toggle"),
      themeToggleLightLabel: text("Switch to light theme"),
      themeToggleDarkLabel: text("Switch to dark theme"),
      openMenuAria: text("Open menu"),
      closeMenuAria: text("Close menu"),
      logoHomeAria: text("SEOH home"),
      mobileShortcutAria: text("Book a 20-min scoping call mobile shortcut"),
      logoEyebrow: text("White-label growth"),
      relatedPathLabel: text("Related path:"),
      seeAlsoLabel: text("See also:"),
      footerEyebrow: text("B2B white-label partner"),
      footerSummary: text(
        "I built SEOH for teams that need senior SEO, AI search, and white-label execution to move from advice to shipped work.",
      ),
      footerTrustStatement: text(
        "SEOH is a senior-led SEO and AI-search consultancy. Every recommendation is evidence-backed, compliance-aware, and reviewed by a human specialist before delivery.",
      ),
      footerServicesGroup: text("Services"),
      footerIndustriesGroup: text("Industries"),
      footerResourcesGroup: text("Resources"),
      footerCompanyGroup: text("Company"),
      footerAvailability: text("Availability"),
      copyrightPrefix: text("Copyright"),
      copyrightSuffix: text(". All Rights Reserved"),
      creditsLine: text("Built for clear search strategy and accessible user journeys."),
      termsOfService: text("Terms of service"),
      privacyPolicy: text("Privacy policy"),
      editorialPolicy: text("Editorial and corrections policy"),
      contactSeoh: text("Contact SEOH"),
      supportPage: text("Support"),
      cancellationPage: text("Cancellation and data deletion"),
      cookiePolicy: text("Cookie policy"),
      acceptableUsePolicy: text("Acceptable use"),
      accessibilityStatement: text("Accessibility statement"),
      adultGateEyebrow: text("Compliance confirmation"),
      adultGateTitle: text("Adult industry services."),
      adultGateBody: text(
        "This page covers SEOH services for lawful 18+ businesses. By continuing you confirm you are visiting on behalf of an adult-industry business and understand all services follow lawful, consent-aware, platform-compliant workflows.",
      ),
      adultGateAccept: text("I confirm - continue"),
      adultGateRefuse: text("Take me back"),
      scrollTopAria: text("Back to top"),
    },
    languageSwitcher: {
      label: text("Language"),
      locales: {
        en: text("English"),
        he: text("Hebrew"),
        ru: text("Russian"),
        ar: text("Arabic"),
        es: text("Spanish"),
        fr: text("French"),
        de: text("German"),
        "pt-br": text("Brazilian Portuguese"),
        ja: text("Japanese"),
        hi: text("Hindi"),
        id: text("Indonesian"),
        "zh-cn": text("Simplified Chinese"),
        tr: text("Turkish"),
        ko: text("Korean"),
        it: text("Italian"),
      },
    },
    industryNavGroups: [
      {
        title: text("Audience-based"),
        items: [
          {
            label: text("Marketing & Digital Agencies"),
            href: staticText("/agencies"),
            snippet: text("White-label fulfillment, reporting, and specialist capacity."),
          },
          {
            label: text("Franchise & Multi-Location Brands"),
            href: staticText("/franchise"),
            snippet: text("Local visibility systems across locations and markets."),
          },
          {
            label: text("Creator Economy"),
            href: staticText("/creators"),
            snippet: text("Brand-safe growth for creator teams and managers."),
          },
          {
            label: text("Adult Industry"),
            href: staticText("/adult"),
            snippet: text("Compliance-first strategy for lawful 18+ businesses."),
          },
        ],
      },
      {
        title: text("Consumer verticals"),
        items: [
          {
            label: text("eCommerce & Consumer Brands"),
            href: staticText("/industries/ecommerce-consumer-brands"),
            snippet: text("Product, category, and AI-search growth."),
            featured: true,
          },
          {
            label: text("Hospitality, Events & Travel"),
            href: staticText("/industries/hospitality-events-travel"),
            snippet: text("Local, seasonal, venue, and travel visibility."),
          },
          {
            label: text("Health & Wellness"),
            href: staticText("/industries/health-wellness"),
            snippet: text("Claims-aware content and AI-search context."),
          },
          {
            label: text("Education"),
            href: staticText("/industries/education"),
            snippet: text("Courses, lessons, and knowledge-center discovery."),
          },
        ],
      },
      {
        title: text("B2B / operational"),
        items: [
          {
            label: text("B2B SaaS & Tech"),
            href: staticText("/industries/saas-tech"),
            snippet: text("Product-led SEO and technical content."),
          },
          {
            label: text("Media & Publishers"),
            href: staticText("/industries/media-publishers"),
            snippet: text("Editorial scale, technical SEO, and AI answers."),
          },
          {
            label: text("Industrial, Distribution & Automotive"),
            href: staticText("/industries/industrial-distribution"),
            snippet: text("Operational B2B visibility and taxonomy."),
          },
          {
            label: text("Professional Services"),
            href: staticText("/industries/professional-services"),
            snippet: text("Authority, local demand, and reputation surfaces."),
          },
        ],
      },
    ],
  },
})], CONTENT_LOCALE_OVERRIDES)[0];

export function resolveSharedChromeContent(locale: AppLocale = DEFAULT_LOCALE) {
  return resolveContent({
    entry: sharedChromeEntry,
    locale,
    persona: "none",
    surface: "canonical",
    indexable: false,
  }).content as unknown as SharedChromeContent;
}

export function getMainLayoutMessages(locale: AppLocale = DEFAULT_LOCALE): MainLayoutMessages {
  return resolveSharedChromeContent(locale).messages;
}

export function getLanguageSwitcherContent(locale: AppLocale = DEFAULT_LOCALE): LanguageSwitcherContent {
  return resolveSharedChromeContent(locale).languageSwitcher;
}

export function getIndustryNavGroups(locale: AppLocale = DEFAULT_LOCALE): readonly IndustryNavGroup[] {
  return resolveSharedChromeContent(locale).industryNavGroups;
}
